import type { TicketKarte, TicketWorkLog } from "@kundeninfo/api-client";
import { formatMenge } from "../../format.js";

/** Aufwand am Ticket (Spec „Tickets: Aufwand“): Stunden lesen, runden, zeigen und in Anteile fassen. */

export const QUARTER_MINUTES = 15;

export type EffortCard = Pick<TicketKarte, "estimatedMinutes" | "workedMinutes" | "estimatedMinutesIncludingChildren"
  | "workedMinutesIncludingChildren" | "subtaskTotal">;

/** A7: "1,5" | "1.5" | "1,5 h" | "1:30" | ",75" → Minuten (ungerundet); leer → null; Unlesbares → undefined. */
export function parseHours(text: string): number | null | undefined {
  const t = text.trim();
  if (t === "") return null;
  const clock = /^(\d+):([0-5]\d)$/.exec(t);
  if (clock !== null) return Number(clock[1]) * 60 + Number(clock[2]);
  const m = /^(\d*)(?:[.,](\d+))?$/.exec(t.replace(/\s*h$/i, ""));
  if (m === null || (m[1] === "" && m[2] === undefined)) return undefined;
  return Number(`${m[1] || "0"}.${m[2] ?? "0"}`) * 60;
}

export function roundQuarter(minutes: number): number {
  return Math.round(minutes / QUARTER_MINUTES) * QUARTER_MINUTES;
}

const hundredths = (minutes: number): number => Math.round(minutes / 60 * 100) / 100;

/** Anzeige mit Tausenderpunkt (P7). */
export function hoursNumber(minutes: number): string {
  return formatMenge(hundredths(minutes));
}

/** Eingabefeld ohne Gruppierung, sonst läse parseHours("1.500") 1,5 h (P7). */
export function hoursInput(minutes: number | null): string {
  return minutes === null ? "" : String(hundredths(minutes)).replace(".", ",");
}

export type EffortShare = { percent: number; fill: number; overrun: number };

/** A11: Anteil Ist/Plan; Füllgrad bis 1, Überschreitung als Anteil des Ist; ohne Plan (null/0) kein Anteil. */
export function effortShare(plan: number | null, worked: number): EffortShare | null {
  if (plan === null || plan <= 0) return null;
  return { percent: Math.round(worked / plan * 100), fill: Math.min(1, worked / plan),
           overrun: worked > plan ? (worked - plan) / worked : 0 };
}

export type ShownEffort = { plan: number | null; worked: number; sum: boolean; own: { plan: number | null; worked: number } };

/** A10/570: mit Teilaufgaben die Werte inklusive Teilbaum (sum), sonst die eigenen. */
export function shownEffort(c: EffortCard): ShownEffort {
  const own = { plan: c.estimatedMinutes, worked: c.workedMinutes };
  return c.subtaskTotal > 0
    ? { plan: c.estimatedMinutesIncludingChildren, worked: c.workedMinutesIncludingChildren, sum: true, own }
    : { ...own, sum: false, own };
}

export function hasEffort(e: { plan: number | null; worked: number }): boolean {
  return e.plan !== null || e.worked > 0;
}

export function effortTotalsOf(log: TicketWorkLog): Pick<TicketKarte, "estimatedMinutes" | "workedMinutes"
  | "estimatedMinutesIncludingChildren" | "workedMinutesIncludingChildren"> {
  return { estimatedMinutes: log.estimatedMinutes, workedMinutes: log.workedMinutes,
           estimatedMinutesIncludingChildren: log.estimatedMinutesIncludingChildren,
           workedMinutesIncludingChildren: log.workedMinutesIncludingChildren };
}
